'use client';

import { useTranslations } from 'next-intl';
import type { JSX } from 'react';

import { LeaderboardSkeleton } from '@/components/social/leaderboard/leaderboard-skeleton';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Alert01Icon,
  ChampionIconSolid,
  Clock04IconSolid,
  InformationCircleIconSolid,
} from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { Link } from '@/i18n/navigation';

/** Route-level skeleton for `/social/competitions/[competitionId]` — full hero, heading, rows. */
export function CompetitionDetailLoading(): JSX.Element {
  const t = useTranslations('competitions.detail');

  return (
    <output
      aria-busy="true"
      data-testid="competition-detail-loading"
      className="flex min-w-0 w-full flex-col gap-6 @container/competition-loading"
    >
      <span className="sr-only">{t('loading')}</span>
      <Panel density="flush" className="h-75 overflow-hidden @max-[767px]/competition-loading:h-92">
        <PanelContent className="flex h-full flex-col justify-between p-5 @max-[767px]/competition-loading:p-4">
          <div className="flex items-center justify-between gap-4" aria-hidden="true">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-16 rounded-pill" />
              <Skeleton className="h-6 w-32 rounded-pill" />
            </div>
            <Skeleton className="h-8 w-36 rounded-pill" />
          </div>
          <div className="grid gap-3" aria-hidden="true">
            <Skeleton className="h-10 w-36 rounded-control" />
            <Skeleton className="h-10 w-2/3 max-w-xl" />
            <Skeleton className="h-5 w-2/5 max-w-sm" />
            <Skeleton className="h-4 w-56" />
          </div>
        </PanelContent>
      </Panel>
      <div className="flex items-center justify-between gap-3" aria-hidden="true">
        <Skeleton className="h-7 w-36" />
        <Skeleton className="h-4 w-44" />
      </div>
      <LeaderboardSkeleton columnCount={4} />
    </output>
  );
}

export function CompetitionDetailError({
  onRetry,
  backHref,
}: {
  onRetry: () => void;
  backHref: string;
}): JSX.Element {
  const t = useTranslations('competitions.detail');

  return (
    <EmptyState
      data-testid="competition-detail-error"
      icon={Alert01Icon}
      size="page"
      title={t('errorTitle')}
      description={t('errorDescription')}
      action={
        <AnimatedButton variant="primary" onClick={onRetry}>
          {t('retry')}
        </AnimatedButton>
      }
    >
      <AnimatedButton asChild variant="tertiary">
        <Link href={backHref}>{t('backToSocial')}</Link>
      </AnimatedButton>
    </EmptyState>
  );
}

export function CompetitionDetailNotFound({ backHref }: { backHref: string }): JSX.Element {
  const t = useTranslations('competitions.detail');

  return (
    <EmptyState
      data-testid="competition-detail-not-found"
      icon={ChampionIconSolid}
      size="page"
      title={t('notFoundTitle')}
      description={t('notFoundDescription')}
      action={
        <AnimatedButton asChild variant="secondary">
          <Link href={backHref}>{t('backToSocial')}</Link>
        </AnimatedButton>
      }
    />
  );
}

export function CompetitionStandingsLoading({ columnCount }: { columnCount: number }): JSX.Element {
  const t = useTranslations('competitions.detail');

  return (
    <output data-testid="competition-standings-loading" aria-busy="true" className="block">
      <span className="sr-only">{t('loading')}</span>
      <LeaderboardSkeleton columnCount={columnCount} />
    </output>
  );
}

export function CompetitionStandingsError({ onRetry }: { onRetry: () => void }): JSX.Element {
  const t = useTranslations('competitions.detail');

  return (
    <EmptyState
      data-testid="competition-standings-error"
      icon={Alert01Icon}
      framed
      title={t('standingsErrorTitle')}
      description={t('standingsErrorDescription')}
      action={
        <AnimatedButton variant="secondary" onClick={onRetry}>
          {t('retry')}
        </AnimatedButton>
      }
    />
  );
}

/** A started competition nobody has qualified for yet — headers with no rows read as broken. */
export function CompetitionStandingsEmpty({ onOpenInfo }: { onOpenInfo: () => void }): JSX.Element {
  const t = useTranslations('competitions.detail');

  return (
    <EmptyState
      data-testid="competition-standings-empty"
      icon={Clock04IconSolid}
      framed
      title={t('emptyTitle')}
      description={t('emptyDescription')}
      action={
        <AnimatedButton
          variant="secondary"
          prefix={InformationCircleIconSolid}
          onClick={onOpenInfo}
        >
          {t('howItWorks')}
        </AnimatedButton>
      }
    />
  );
}

export function CompetitionStandingsUpcoming({
  startLabel,
  onOpenInfo,
}: {
  startLabel: string;
  onOpenInfo: () => void;
}): JSX.Element {
  const t = useTranslations('competitions.detail');

  return (
    <EmptyState
      data-testid="competition-standings-upcoming"
      icon={Clock04IconSolid}
      framed
      title={t('upcomingTitle')}
      description={t('upcomingDescription', { start: startLabel })}
      action={
        <AnimatedButton
          variant="secondary"
          prefix={InformationCircleIconSolid}
          onClick={onOpenInfo}
        >
          {t('howItWorks')}
        </AnimatedButton>
      }
    />
  );
}
